import React, { useState, useEffect, useMemo } from 'react';
import { doc, setDoc, onSnapshot, getDoc, updateDoc } from 'firebase/firestore';
import { ArrowLeft, UserCog, Search, UserPlus, Merge, Edit, Ticket } from 'lucide-react';
import { db, APP_ID_INTERNO } from '../firebase';
import './PlayerManagement.css';

export default function PlayerManagement({ tournaments, onBack, role }) {
    const [basePointsData, setBasePointsData] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const bpRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'puntosBase');
        const unsubscribe = onSnapshot(bpRef, (docSnap) => {
            if (docSnap.exists()) {
                setBasePointsData(docSnap.data() || {});
            } else {
                setBasePointsData({});
            }
        });
        return () => unsubscribe();
    }, []);

    const playersList = useMemo(() => {
        const map = {};
        
        tournaments.forEach(t => {
            (t.players || []).forEach(p => {
                const key = p.name.trim().toUpperCase();
                if (!map[key]) map[key] = p.name.trim();
            });
        });

        Object.keys(basePointsData).forEach(key => {
            if (!map[key]) map[key] = basePointsData[key].originalName;
        });

        return Object.keys(map).map(k => ({
            key: k,
            name: map[k],
            points: basePointsData[k]?.points || 0,
            bonos: basePointsData[k]?.bonos || 0
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [tournaments, basePointsData]);

    const handleSaveBasePoints = async (nameKey, originalName, newPoints) => {
        if (role !== 'admin') return;
        const bpRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'puntosBase');
        await setDoc(bpRef, {
            [nameKey]: { originalName, points: Number(newPoints) || 0 }
        }, { merge: true });
    };

    const handleSaveBonos = async (nameKey, originalName, newBonos) => {
        if (role !== 'admin') return;
        const bpRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'puntosBase');
        await setDoc(bpRef, {
            [nameKey]: { originalName, bonos: Number(newBonos) || 0 }
        }, { merge: true });
    };

    const handleAddPlayer = async () => {
        if (role !== 'admin') return;
        const name = window.prompt("Nombre del nuevo jugador:");
        if (!name || !name.trim()) return;
        
        const newKey = name.trim().toUpperCase();
        if (playersList.find(p => p.key === newKey)) {
            return window.alert("Este jugador ya existe en la base de datos.");
        }

        const bpRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'puntosBase');
        await setDoc(bpRef, {
            [newKey]: { originalName: name.trim(), points: 0, bonos: 0 }
        }, { merge: true });
    };

    const handleEditName = async (oldKey, oldName) => {
        if (role !== 'admin') return;
        const newName = window.prompt(`Modificar nombre de ${oldName}:`, oldName);
        if (!newName || newName.trim() === '' || newName.trim() === oldName) return;

        const newKey = newName.trim().toUpperCase();

        if (playersList.find(p => p.key === newKey)) {
            return window.alert("Ya existe un jugador con ese nombre. Si quieres unirlos, usa el botón de Fusionar (naranja).");
        }

        if (window.confirm(`¿Seguro que quieres cambiar el nombre de "${oldName}" a "${newName}"?\nSe actualizará automáticamente en todos los torneos pasados.`)) {
            try {
                const bpRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'puntosBase');
                let newBpData = { ...basePointsData };
                if (newBpData[oldKey]) {
                    newBpData[newKey] = { ...newBpData[oldKey], originalName: newName.trim() };
                    delete newBpData[oldKey];
                    await setDoc(bpRef, newBpData);
                } else {
                    await setDoc(bpRef, { [newKey]: { originalName: newName.trim(), points: 0 } }, { merge: true });
                }

                const adjRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'manuales');
                const adjSnap = await getDoc(adjRef);
                if (adjSnap.exists()) {
                    let adjData = adjSnap.data();
                    if (adjData[oldKey]) {
                        adjData[newKey] = { ...adjData[oldKey], originalName: newName.trim() };
                        delete adjData[oldKey];
                        await setDoc(adjRef, adjData);
                    }
                }

                for (let t of tournaments) {
                    let needsUpdate = false;
                    let updatedPlayers = [...(t.players || [])];
                    let updatedResults = t.leagueResults ? [...t.leagueResults] : [];

                    updatedPlayers = updatedPlayers.map(p => {
                        if (p.name.trim().toUpperCase() === oldKey) {
                            needsUpdate = true;
                            return { ...p, name: newName.trim() };
                        }
                        return p;
                    });

                    updatedResults = updatedResults.map(r => {
                        if (r.name.trim().toUpperCase() === oldKey) {
                            needsUpdate = true;
                            return { ...r, name: newName.trim() };
                        }
                        return r;
                    });

                    if (needsUpdate) {
                        await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', t.id), {
                            players: updatedPlayers,
                            ...(t.leagueResults ? { leagueResults: updatedResults } : {})
                        });
                    }
                }
                
                window.alert("Nombre actualizado correctamente en todo el sistema.");
            } catch (error) {
                console.error(error);
                window.alert("Hubo un error al actualizar el nombre.");
            }
        }
    };

    const handleMergePlayer = async (oldKey, oldName) => {
        if (role !== 'admin') return;
        const targetName = window.prompt(`🌀 FUSIÓN DE JUGADORES\n\nVas a fusionar a "${oldName}" para sumarle todos sus puntos a otro jugador.\n\nEscribe el nombre EXACTO del jugador destino (el que se mantendrá):`);
        if (!targetName || targetName.trim() === '') return;

        const targetKey = targetName.trim().toUpperCase();

        if (targetKey === oldKey) {
            return window.alert("No puedes fusionar a un jugador consigo mismo.");
        }

        const targetExists = playersList.find(p => p.key === targetKey);
        if (!targetExists) {
            return window.alert(`No se ha encontrado a ningún jugador llamado "${targetName}". Si lo que quieres es cambiarle el nombre, usa el botón del lápiz (Editar).`);
        }

        if (window.confirm(`⚠️ ATENCIÓN ⚠️\n\nVas a fusionar a "${oldName}" dentro de "${targetExists.name}".\n\n- Todos los historiales de torneos de ${oldName} pasarán a ser de ${targetExists.name}.\n- Sus Puntos Base y Ajustes se sumarán.\n- "${oldName}" desaparecerá.\n\n¿Proceder con la fusión?`)) {
            try {
                const bpRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'puntosBase');
                let newBpData = { ...basePointsData };
                let oldBp = newBpData[oldKey]?.points || 0;
                let targetBp = newBpData[targetKey]?.points || 0;
                let oldBonos = newBpData[oldKey]?.bonos || 0;
                let targetBonos = newBpData[targetKey]?.bonos || 0;
                
                newBpData[targetKey] = { 
                    originalName: targetExists.name, 
                    points: targetBp + oldBp,
                    bonos: targetBonos + oldBonos 
                };
                delete newBpData[oldKey];
                await setDoc(bpRef, newBpData);

                const adjRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'manuales');
                const adjSnap = await getDoc(adjRef);
                if (adjSnap.exists()) {
                    let adjData = adjSnap.data();
                    let oldAdj = adjData[oldKey]?.points || 0;
                    let targetAdj = adjData[targetKey]?.points || 0;
                    
                    if (adjData[oldKey] || adjData[targetKey]) {
                        adjData[targetKey] = { originalName: targetExists.name, points: targetAdj + oldAdj, deleted: false };
                        delete adjData[oldKey];
                        await setDoc(adjRef, adjData);
                    }
                }

                for (let t of tournaments) {
                    let needsUpdate = false;
                    let updatedPlayers = [...(t.players || [])];
                    let updatedResults = t.leagueResults ? [...t.leagueResults] : [];

                    updatedPlayers = updatedPlayers.map(p => {
                        if (p.name.trim().toUpperCase() === oldKey) {
                            needsUpdate = true;
                            return { ...p, name: targetExists.name };
                        }
                        return p;
                    });

                    updatedResults = updatedResults.map(r => {
                        if (r.name.trim().toUpperCase() === oldKey) {
                            needsUpdate = true;
                            return { ...r, name: targetExists.name };
                        }
                        return r;
                    });

                    if (needsUpdate) {
                        await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', t.id), {
                            players: updatedPlayers,
                            ...(t.leagueResults ? { leagueResults: updatedResults } : {})
                        });
                    }
                }
                
                window.alert("¡Fusión completada con éxito!");
            } catch (error) {
                console.error(error);
                window.alert("Hubo un error durante la fusión.");
            }
        }
    };

    const filteredPlayers = playersList.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="pm-container">
            <button onClick={onBack} className="btn btn-secondary" style={{width: 'fit-content'}}>
                <ArrowLeft className="w-4 h-4"/> Volver
            </button>

            <div className="pm-header">
                <UserCog style={{width: '6rem', height: '6rem', color: 'var(--accent-cyan)', filter: 'drop-shadow(0 0 10px rgba(102, 252, 241, 0.5))'}} />
                <h2 className="pm-title">
                    Base de Datos de Bladers
                </h2>
                <p className="pm-subtitle">
                    Gestiona Perfiles, Fusiones y Puntos Base
                </p>
            </div>

            <div className="pm-panel">
                <div className="pm-search-bar">
                    <div className="pm-search-input-wrapper">
                        <Search style={{color: 'var(--text-secondary)', width: '1.5rem', height: '1.5rem'}} />
                        <input 
                            type="text" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            placeholder="Buscar blader..." 
                            className="pm-search-input"
                        />
                    </div>
                    {role === 'admin' && (
                        <button onClick={handleAddPlayer} className="btn btn-primary" style={{width: '100%'}}>
                            <UserPlus className="w-4 h-4"/> Añadir
                        </button>
                    )}
                </div>

                <div className="pm-grid">
                    {filteredPlayers.map(p => (
                        <div key={p.key} className="pm-card glass-panel" style={{padding: '1rem'}}>
                            
                            {/* Fila Superior: Nombre y Botones de Acción */}
                            <div className="pm-card-top">
                                <span className="pm-card-name text-gradient">{p.name}</span>
                                {role === 'admin' && (
                                    <div className="pm-card-actions">
                                        <button onClick={() => handleMergePlayer(p.key, p.name)} className="pm-btn-icon orange" title="Fusionar (Unir a otro jugador)">
                                            <Merge className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleEditName(p.key, p.name)} className="pm-btn-icon blue" title="Editar nombre">
                                            <Edit className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Fila Inferior: Inputs de Puntos y Bonos */}
                            <div className="pm-card-bottom">
                                <div className="pm-input-group">
                                    <Ticket className="w-4 h-4 text-purple-500" style={{color: '#9b59b6'}} />
                                    <span className="pm-input-label">Bonos:</span>
                                    <BasePointInput 
                                        playerKey={p.key} 
                                        playerName={p.name} 
                                        currentPoints={p.bonos} 
                                        onSave={handleSaveBonos} 
                                    />
                                </div>
                                <div className="pm-input-group">
                                    <span className="pm-input-label">P. Base:</span>
                                    <BasePointInput 
                                        playerKey={p.key} 
                                        playerName={p.name} 
                                        currentPoints={p.points} 
                                        onSave={handleSaveBasePoints} 
                                    />
                                </div>
                            </div>

                        </div>
                    ))}
                    {filteredPlayers.length === 0 && (
                        <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '2.5rem 0', color: 'var(--text-secondary)', fontWeight: '700', textTransform: 'uppercase', fontStyle: 'italic', letterSpacing: '2px'}}>
                            No se encontraron bladers.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function BasePointInput({ playerKey, playerName, currentPoints, onSave }) {
    const [val, setVal] = useState(currentPoints);
    
    useEffect(() => {
        setVal(currentPoints);
    }, [currentPoints]);

    const handleBlur = () => {
        if (Number(val) !== currentPoints) {
            onSave(playerKey, playerName, val);
        }
    };

    return (
        <input 
            type="number" 
            value={val} 
            onChange={(e) => setVal(e.target.value)} 
            onBlur={handleBlur}
            className="pm-number-input"
        />
    );
}